import { eenmaligeStart } from "./eenmaligeStart";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { PleegKeuze, type PleegKeus } from "./PleegKeuze";
import { CameraPawIcon } from "./CameraPawIcon";
import { FilterInklapKnop, useFilterInklap } from "./FilterInklap";
import { useCustom, useTranslate, useGetLocale } from "@refinedev/core";
import { useTabelIndeling } from "../hooks/useTabelIndeling";
import {
  DateField,
  AnimalCard,
  Button,
  DataTable,
  Modal,
  CheckboxField,
  PawIcon,
  CalendarIcon,
  ClockIcon,
  HeartIcon,
  ScissorsIcon,
  UserIcon,
  CheckIcon,
  HomeIcon,
  ShieldIcon,
  PinIcon,
  GearIcon,
  LoginIcon,
  ScaleIcon,
  SyringeIcon,
  InfoIcon,
  BookIcon,
  type AnimalCardMetaRow,
  type Column,
} from "@fawkes/ui";

import { formatDate } from "../i18n/format";
import { useAnimalPhotoUrls } from "../api/useAnimalPhotoUrls";

// ── wire types (GET /api/animals/present) ─────────────────────────────────────────────────────────
interface CardItem {
  id: number;
  verblijfId: number;
  verblijfNummer: number | null;
  /** Het dierregistratienummer van de opvang; staat voor de naam. */
  registratieNummer: number | null;
  naam: string | null;
  fotoUrl: string | null;
  diersoort: string | null;
  ras: string | null;
  kruising: boolean;
  kleur: string | null;
  vacht: string | null;
  geslacht: string | null;
  gecastreerd: boolean;
  castratieDatum: string | null;
  geboortedatum: string | null;
  opnameDatum: string | null;
  daysInShelter: number | null;
  herkomst: string | null;
  kenmerken: string | null;
  chipPasnummer: string | null;
  paspoortNummer: string | null;
  gewichtKg: string | null;
  /** ONDER, BINNEN, BOVEN of GEEN ten opzichte van het streefgewicht; leeg bij geplaatste dieren. */
  gewichtSignaal?: string | null;
  /** Nooit gewogen, of de laatste weging is ouder dan 30 dagen. */
  gewichtVerlopen?: boolean;
  entingenOk: boolean;
  ontwormenOk: boolean;
  ontvlooienOk: boolean;
  /** Dagen in de lopende pleegplaatsing; leeg als het dier niet in een pleeggezin zit. */
  pleegDagen: number | null;
  foster: boolean;
  // Server-provided status badge: null for present (SPA computes "In asiel sinds N dagen"), set for
  // placed animals to "Vertrokken op {datum}".
  statusTekst: string | null;
}
interface SpeciesCount {
  id: number | null;
  naam: string;
  count: number;
}
interface AttendanceResponse {
  items: CardItem[];
  total: number;
  speciesCounts: SpeciesCount[];
  fosterCount: number;
  fosterModuleEnabled: boolean;
  /** Alle dieren van het overzicht, los van de filters: de tegel "Totaal". */
  totaalAlle?: number;
}
interface RefOption {
  id: number;
  label: string;
}

type ViewMode = "card" | "compact" | "list";

// The 18 selectable card fields, in Jmix `DierAttendanceCardField` order (drives chooser + card order).
const ALL_FIELDS = [
  "foto", "ras", "kleur", "vacht", "leeftijd", "geboortedatum", "geslacht", "gecastreerd",
  "status", "herkomst", "diersoort", "opnamedatum", "gewicht", "gewichtsignaal", "entingen", "bijzonderheden",
  "castratiedatum", "paspoortnummer", "chipnummer", "ontwormen", "ontvlooien", "pleegdagen",
] as const;
type FieldKey = (typeof ALL_FIELDS)[number];
const DEFAULT_FIELDS: FieldKey[] = ["ras", "kleur", "leeftijd", "status", "herkomst", "chipnummer"];
// ras/chipnummer/foto are rendered specially (subtitle / footer / photo), not as meta rows.
const META_FIELDS = ALL_FIELDS.filter((f) => f !== "ras" && f !== "chipnummer" && f !== "foto");

// Ground-truth glyphs from frontend-react/dier-aanwezig (FIELDS): kleur=heart, vacht=scissors,
// geboortedatum=clock, geslacht=user, opnamedatum=login, gewicht=scale, entingen=syringe,
// bijzonderheden=info, paspoort=book — matched icon-for-icon with acc.
const FIELD_ICON: Record<string, ReactNode> = {
  kleur: <HeartIcon width={16} height={16} />,
  vacht: <ScissorsIcon width={16} height={16} />,
  leeftijd: <CalendarIcon width={16} height={16} />,
  geboortedatum: <ClockIcon width={16} height={16} />,
  geslacht: <UserIcon width={16} height={16} />,
  gecastreerd: <CheckIcon width={16} height={16} />,
  status: <HomeIcon width={16} height={16} />,
  herkomst: <PinIcon width={16} height={16} />,
  diersoort: <PawIcon width={16} height={16} />,
  opnamedatum: <LoginIcon width={16} height={16} />,
  gewicht: <ScaleIcon width={16} height={16} />,
  gewichtsignaal: <ScaleIcon width={16} height={16} />,
  entingen: <SyringeIcon width={16} height={16} />,
  ontwormen: <ShieldIcon width={16} height={16} />,
  ontvlooien: <ShieldIcon width={16} height={16} />,
  pleegdagen: <HomeIcon width={16} height={16} />,
  bijzonderheden: <InfoIcon width={16} height={16} />,
  castratiedatum: <CalendarIcon width={16} height={16} />,
  paspoortnummer: <BookIcon width={16} height={16} />,
};

const CHIP_LOOKUP = "https://www.petbase.eu/chipnummer-zoeken/?chip_number=";

export interface AnimalOverviewProps {
  /** Relative API url for the feed (dataProvider prepends /api). Default: the present-animals query. */
  endpoint?: string;
  /** Open the animal detail (card click + Details button + list row). */
  onOpenAnimal: (id: string) => void;
  /** Per-species stat cards in the header (present view: on). */
  showStatCards?: boolean;
  /** Foster toggle (only rendered when the module is enabled per the feed). */
  showFosterToggle?: boolean;
  /** localStorage prefix for the client-persisted view-mode + field selection. */
  prefsKey?: string;
  /** Header title i18n key (default: the present-animals title; placed passes its own). */
  titleKey?: string;
  /** Header subtitle i18n key (default: the present-animals subtitle). */
  subtitleKey?: string;
  /**
   * Toont de vertrekfilters (vertrekperiode + hoe het dier vertrok) in de tweede rij. Alleen zinvol op
   * het vertrokken-overzicht: bij aanwezige dieren is er nog geen vertrek om op te filteren.
   */
  showDepartureFilters?: boolean;
}

/**
 * Reusable animal overview — the card/compact/list grid behind "Dier aanwezig", built to also power the
 * general animals list later (parameterised by {@link AnimalOverviewProps.endpoint} + the stat/foster
 * toggles). Faithful port of the Jmix `DierAttendanceOverviewView`: search, species multi-select, breed,
 * age band, dumped, min-days, foster toggle, two sorts, server-side paging, clickable per-species stat
 * cards, a field chooser and three view modes. View-mode + field selection persist client-side (the one
 * deliberate divergence from Jmix's server-backed UserSettings; see the migration plan).
 */
/**
 * Diersoort multi-select — a faithful stand-in for the Jmix `MultiSelectComboBox`: a filter-styled
 * button summarising the selection, opening a checkbox list. Bound to the shared `speciesIds` state so
 * the stat cards and this control stay in sync (both multi-select). Closes on outside-click / Escape.
 */
function SpeciesMultiSelect({
  options,
  selected,
  onToggle,
  onClear,
  allLabel,
  selectedLabel,
}: {
  options: RefOption[];
  selected: number[];
  onToggle: (id: number) => void;
  onClear: () => void;
  allLabel: string;
  selectedLabel: (n: number) => string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const summary =
    selected.length === 0
      ? allLabel
      : selected.length === 1
        ? (options.find((o) => o.id === selected[0])?.label ?? selectedLabel(1))
        : selectedLabel(selected.length);

  return (
    <div className="da-overview__multiselect" ref={ref}>
      <button
        type="button"
        className="da-overview__filter da-overview__multiselect-btn"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="da-overview__multiselect-summary">{summary}</span>
        <span className="da-overview__multiselect-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="da-overview__multiselect-menu">
          <label className="da-overview__multiselect-option">
            <input type="checkbox" checked={selected.length === 0} onChange={onClear} />
            {allLabel}
          </label>
          {options.map((o) => (
            <label key={o.id} className="da-overview__multiselect-option">
              <input type="checkbox" checked={selected.includes(o.id)} onChange={() => onToggle(o.id)} />
              {o.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export function AnimalOverview({
  endpoint = "animals/present",
  onOpenAnimal,
  showStatCards = true,
  showFosterToggle = true,
  prefsKey = "dierAanwezig",
  titleKey = "dierAanwezig.title",
  subtitleKey = "dierAanwezig.subtitle",
  showDepartureFilters = false,
}: AnimalOverviewProps) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  // ── filter + paging state ─────────────────────────────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [speciesIds, setSpeciesIds] = useState<number[]>([]);
  const [rasId, setRasId] = useState<string>("");
  const [ageBand, setAgeBand] = useState("ALL");
  // "Tussen…": een eigen leeftijdsbereik (van inclusief, tot exclusief), in maanden of jaren. Jonge dieren
  // hebben een eigen zorgbehoefte, dus "jonger dan 3 / 6 maanden" moeten als peiler op te vragen zijn.
  const [ageVan, setAgeVan] = useState("");
  const [ageVanEenheid, setAgeVanEenheid] = useState<AgeUnit>("M");
  const [ageTot, setAgeTot] = useState("");
  const [ageTotEenheid, setAgeTotEenheid] = useState<AgeUnit>("M");
  // Het bereik kies je in een popupje onder het veld; pas "Toepassen" zet het filter, zodat de lijst niet
  // bij elke toetsaanslag opnieuw laadt. Annuleren zonder eerder bereik zet de vorige keuze terug.
  const [ageOpen, setAgeOpen] = useState(false);
  const [ageDraft, setAgeDraft] = useState<{ van: string; vanE: AgeUnit; tot: string; totE: AgeUnit }>({
    van: "", vanE: "M", tot: "", totE: "M",
  });
  const agePrevBand = useRef("ALL");
  const agePopRef = useRef<HTMLDivElement | null>(null);
  const openAgeRange = () => {
    setAgeDraft({ van: ageVan, vanE: ageVanEenheid, tot: ageTot, totE: ageTotEenheid });
    setAgeOpen(true);
  };
  const applyAgeRange = () => {
    setAgeVan(ageDraft.van.trim());
    setAgeVanEenheid(ageDraft.vanE);
    setAgeTot(ageDraft.tot.trim());
    setAgeTotEenheid(ageDraft.totE);
    setAgeOpen(false);
    if (!ageDraft.van.trim() && !ageDraft.tot.trim()) setAgeBand(agePrevBand.current);
  };
  const cancelAgeRange = () => {
    setAgeOpen(false);
    if (!ageVan && !ageTot) setAgeBand(agePrevBand.current);
  };
  const cancelAgeRangeRef = useRef(cancelAgeRange);
  cancelAgeRangeRef.current = cancelAgeRange;
  useEffect(() => {
    if (!ageOpen) return;
    const onDown = (e: MouseEvent) => {
      if (agePopRef.current && !agePopRef.current.contains(e.target as Node)) cancelAgeRangeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") cancelAgeRangeRef.current();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [ageOpen]);
  const [dumped, setDumped] = useState("ALL");
  // Gewicht: onder, boven of buiten het streefgewicht, of weging verlopen. Alleen bij aanwezige dieren; de
  // dashboardtegel Gewichtssignalen opent dit scherm met dit filter al gezet.
  // Het filter waarmee de dashboardtegel Gewichtssignalen dit overzicht opent; één keer gelezen.
  const [vanTegel] = useState(() => eenmaligeStart("dierAanwezig.gewichtFilter"));
  const [gewicht, setGewicht] = useState(vanTegel);
  const gewichtFilterAan = endpoint === "animals/present";
  // "X dieren langer dan 60 dagen" op het dashboard opent dit overzicht met dat minimum al gezet.
  const [dagenVanTegel] = useState(() => {
    const v = eenmaligeStart("dierAanwezig.minDagen");
    return /^\d+$/.test(v) ? v : "";
  });
  const [minDays, setMinDays] = useState(dagenVanTegel || "0");
  // Het dashboard telt alle aanwezige dieren, ook die in een pleeggezin. Kom je daarvandaan, dan staan ze er hier
  // ook bij: anders zei de tegel "1 dier buiten streefgewicht" en was de lijst leeg (Roze, 2026-10-09).
  const [pleeg, setPleeg] = useState<PleegKeus>(() =>
    eenmaligeStart("dierAanwezig.pleeg") === "beide" || vanTegel || dagenVanTegel ? "beide" : "geen");
  // De tweede rij: opname- en vertrekperiode, hoe het dier vertrok en waar het is aangetroffen.
  // Ingeklapt tenzij er iets in staat — anders is de balk twee rijen hoog voor filters die je
  // zelden gebruikt, en met een gezet filter dat je niet ziet is nog erger dan een rij te veel.
  const [opnameVan, setOpnameVan] = useState("");
  const [opnameTot, setOpnameTot] = useState("");
  const [vertrekVan, setVertrekVan] = useState("");
  const [vertrekTot, setVertrekTot] = useState("");
  const [vertrekSoortId, setVertrekSoortId] = useState("");
  const [gemeenteId, setGemeenteId] = useState("");
  const [meerOpen, setMeerOpen] = useState(false);
  const [sort, setSort] = useState("NEWEST");
  const [page, setPage] = useState(0);
  // Source parity: PER_PAGINA = 20 (the Jmix `dier-aanwezig` page size).
  const size = 20;

  // ── view-mode + field selection (client-persisted) ───────────────────────────────────────────
  const [viewMode, setViewMode] = useState<ViewMode>(() => readMode(prefsKey));
  const [fields, setFields] = useState<FieldKey[]>(() => readFields(prefsKey));
  const [chooserOpen, setChooserOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Reset to the first page whenever a filter changes.
  useEffect(() => {
    setPage(0);
  }, [speciesIds, rasId, ageBand, ageVan, ageVanEenheid, ageTot, ageTotEenheid, dumped, minDays, pleeg, sort,
      opnameVan, opnameTot, vertrekVan, vertrekTot, vertrekSoortId, gemeenteId]);

  // The breed list is re-scoped when a single species is picked; clear a now-out-of-range breed so it
  // can't linger as an invisible dead filter.
  useEffect(() => {
    setRasId("");
  }, [speciesIds]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (search) p.set("q", search);
    speciesIds.forEach((id) => p.append("speciesIds", String(id)));
    if (rasId) p.set("rasId", rasId);
    const leeftijd = ageBandBounds(ageBand, ageVan, ageVanEenheid, ageTot, ageTotEenheid);
    if (leeftijd) {
      for (const [kant, grens] of [["From", leeftijd.from], ["To", leeftijd.to]] as const) {
        if (grens == null) continue;
        p.set(grens.unit === "W" ? `age${kant}Weeks` : `age${kant}Months`, String(grens.value));
      }
    } else if (ageBand !== "ALL") {
      p.set("ageBand", ageBand);
    }
    if (dumped !== "ALL") p.set("dumped", dumped);
    if (gewichtFilterAan && gewicht) p.set("gewicht", gewicht);
    if (Number(minDays) > 0) p.set("minDays", minDays);
    // "Beide" haalt ze erbij, "alleen" laat de rest weg -- dat laatste moet de server doen, want de
    // lijst is gepagineerd en in de browser filteren laat de paginering en de tellingen liegen.
    if (pleeg === "beide") p.set("includeFoster", "true");
    if (pleeg === "alleen") p.set("fosterOnly", "true");
    if (opnameVan) p.set("opnameVan", opnameVan);
    if (opnameTot) p.set("opnameTot", opnameTot);
    if (vertrekVan) p.set("vertrekVan", vertrekVan);
    if (vertrekTot) p.set("vertrekTot", vertrekTot);
    if (vertrekSoortId) p.set("vertrekSoortId", vertrekSoortId);
    if (gemeenteId) p.set("gemeenteId", gemeenteId);
    p.set("sort", sort);
    p.set("page", String(page));
    p.set("size", String(size));
    return p.toString();
  }, [search, speciesIds, rasId, ageBand, ageVan, ageVanEenheid, ageTot, ageTotEenheid, dumped, gewicht, gewichtFilterAan, minDays, pleeg, sort, page, size,
      opnameVan, opnameTot, vertrekVan, vertrekTot, vertrekSoortId, gemeenteId]);

  // keepPreviousData: bij een filterwijziging blijft de vorige lijst staan tot de nieuwe binnen is, in
  // plaats van dat het scherm leegslaat naar "Laden…". Je ziet dan wát er verandert; nu leek elke
  // filterklik het scherm opnieuw op te bouwen.
  const { data, isLoading, isError, isFetching } = useCustom<AttendanceResponse>({
    url: `${endpoint}?${query}`,
    method: "get",
    queryOptions: { keepPreviousData: true },
  });
  const result = data?.data;
  const items = result?.items ?? [];
  const total = result?.total ?? 0;
  const fosterModuleEnabled = result?.fosterModuleEnabled ?? false;
  const pageCount = Math.max(1, Math.ceil(total / size));

  // Load each visible animal's uploaded photo (authenticated blob → object URL). The feed's `fotoUrl`
  // is the *online* image and is null for shelter-uploaded animals; this fills the gap so cards show the
  // real photo like acc. `photoOf` prefers the loaded local photo, falling back to any online url.
  const photoUrls = useAnimalPhotoUrls(items.map((a) => a.id));
  const photoOf = (a: CardItem): string | null => photoUrls[String(a.id)] ?? a.fotoUrl;

  // Breed options for the current species filter (or all present breeds when no species chosen).
  const breedUrl = speciesIds.length === 1
    ? `animals/reference/breeds?species=${speciesIds[0]}`
    : "animals/reference/breeds";
  const { data: breedData } = useCustom<RefOption[]>({ url: breedUrl, method: "get" });
  const breeds = breedData?.data ?? [];

  // (The vestigingen/current fetch that backed the subtitle is gone: the source's subtitle is a fixed
  // description, and the sidebar already names the vestiging.)
  const { data: speciesData } = useCustom<RefOption[]>({ url: "animals/reference/species", method: "get" });
  // Alleen ophalen als de tweede rij ooit is opengeklapt: wie hem nooit gebruikt hoeft er ook niet
  // bij elke schermopening twee verzoeken voor te betalen.
  const { data: vertrekSoortData } = useCustom<RefOption[]>({
    url: "animals/reference/departure-types",
    method: "get",
    queryOptions: { enabled: showDepartureFilters && meerOpen },
  });
  const { data: gemeenteData } = useCustom<RefOption[]>({
    url: "animals/reference/gemeenten",
    method: "get",
    queryOptions: { enabled: meerOpen },
  });
  const vertrekSoorten = vertrekSoortData?.data ?? [];
  const gemeenten = gemeenteData?.data ?? [];
  const speciesOptions = speciesData?.data ?? [];

  const toggleSpecies = (id: number) =>
    setSpeciesIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));

  // Wat er in de tweede rij aanstaat. Alleen om op de knop te tonen: een filter dat je niet ziet
  // omdat de rij dicht staat, verklaart anders nooit waarom de lijst zo kort is.
  const meerActief = [opnameVan, opnameTot, vertrekVan, vertrekTot, vertrekSoortId, gemeenteId]
    .filter(Boolean).length;

  // Op een telefoon staat het hele filterblok dicht en blijft alleen het zoekveld staan; zie
  // FilterInklap voor het waarom. Op een breed scherm is `toon` altijd waar en `mobiel` nooit,
  // dus daar staat er geen knop en verandert er niets. Het aantal op de knop telt álle gezette
  // filters, ook die van de tweede rij — anders zou een filter twee klikken diep onzichtbaar zijn.
  const { mobiel, open: filtersOpen, toon, wissel } = useFilterInklap();
  const filterActief =
    (speciesIds.length > 0 ? 1 : 0) +
    (rasId ? 1 : 0) +
    (ageBand !== "ALL" ? 1 : 0) +
    (dumped !== "ALL" ? 1 : 0) +
    (gewichtFilterAan && gewicht ? 1 : 0) +
    (minDays !== "0" && minDays !== "" ? 1 : 0) +
    meerActief;

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setSpeciesIds([]);
    setRasId("");
    setAgeBand("ALL");
    setAgeVan("");
    setAgeTot("");
    setAgeOpen(false);
    setDumped("ALL");
    setGewicht("");
    setMinDays("0");
    setPleeg("geen");
    setOpnameVan("");
    setOpnameTot("");
    setVertrekVan("");
    setVertrekTot("");
    setVertrekSoortId("");
    setGemeenteId("");
    setSort("NEWEST");
    setPage(0);
  };

  const persistMode = (m: ViewMode) => {
    setViewMode(m);
    try {
      localStorage.setItem(`${prefsKey}.viewMode`, m);
    } catch {
      /* private mode / quota — fall back to in-memory only */
    }
  };

  // ── per-card formatting (mirrors the Jmix renderer) ───────────────────────────────────────────
  const jaNee = (v: boolean) => t(v ? "dierAanwezig.yes" : "dierAanwezig.no");
  const subtitleOf = (a: CardItem) => formatRace(a.ras, a.kruising, a.diersoort, t);
  const statusText = (a: CardItem) =>
    // Source parity: a server status (placed → "Vertrokken op {datum}") wins; present has none, so the
    // SPA computes "In asiel sinds N dagen" from the days count.
    a.statusTekst ??
    (a.daysInShelter == null
      ? null
      : `${t("dierAanwezig.card.inShelterSince")} ${a.daysInShelter} ${t(a.daysInShelter === 1 ? "dierAanwezig.card.day" : "dierAanwezig.card.days")}`);

  const valueOf = (a: CardItem, f: FieldKey): string => {
    const na = "-";
    switch (f) {
      case "kleur": return a.kleur || na;
      case "vacht": return a.vacht || na;
      case "leeftijd": return formatAge(a.geboortedatum) ?? na;
      case "geboortedatum": return a.geboortedatum ? formatDate(a.geboortedatum, locale) : na;
      case "geslacht": return a.geslacht || na;
      case "gecastreerd": return jaNee(a.gecastreerd);
      case "status": return statusText(a) ?? na;
      case "herkomst": return a.herkomst || na;
      case "diersoort": return a.diersoort || na;
      case "opnamedatum": return a.opnameDatum ? formatDate(a.opnameDatum, locale) : na;
      case "gewicht": return a.gewichtKg ? `${a.gewichtKg} kg` : na;
      case "gewichtsignaal": return gewichtSignaalTekst(a) ?? na;
      case "entingen": return jaNee(a.entingenOk);
      case "ontwormen": return jaNee(a.ontwormenOk);
      case "ontvlooien": return jaNee(a.ontvlooienOk);
      // Geen plaatsing, geen getal: "0 dagen" zou beweren dat het dier er vandaag heen ging.
      case "pleegdagen": return a.pleegDagen == null ? "—"
        : t(a.pleegDagen === 1 ? "dierAanwezig.veld.pleegdagEen" : "dierAanwezig.veld.pleegdagen",
            { args: [a.pleegDagen] });
      case "bijzonderheden": return a.kenmerken || na;
      case "castratiedatum": return a.castratieDatum ? formatDate(a.castratieDatum, locale) : na;
      case "paspoortnummer": return a.paspoortNummer || na;
      default: return na;
    }
  };

  // Het gewichtssignaal: op de kaart een symbool rechtsboven op de foto, in de lijst een kolom.
  const gewichtSignaalTekst = (a: CardItem): string | null => {
    const delen = [
      a.gewichtSignaal === "ONDER" ? t("dierAanwezig.gewicht.onder")
        : a.gewichtSignaal === "BOVEN" ? t("dierAanwezig.gewicht.boven")
        : a.gewichtSignaal === "BINNEN" ? t("dierAanwezig.gewicht.binnen") : null,
      a.gewichtVerlopen ? t("dierAanwezig.gewicht.verlopen") : null,
    ].filter(Boolean);
    return delen.length ? delen.join(" · ") : a.gewichtSignaal ? t("dierAanwezig.gewicht.geen") : null;
  };
  const gewichtSignaalVan = (a: CardItem) => {
    if (!fields.includes("gewichtsignaal") || !a.gewichtSignaal) return null;
    const label = gewichtSignaalTekst(a);
    if (!label) return null;
    if (a.gewichtSignaal === "ONDER") return { icoon: <ArrowDown size={16} aria-hidden="true" />, label, toon: "danger" as const };
    if (a.gewichtSignaal === "BOVEN") return { icoon: <ArrowUp size={16} aria-hidden="true" />, label, toon: "warning" as const };
    if (a.gewichtVerlopen) return { icoon: <ClockIcon width={16} height={16} />, label, toon: "muted" as const };
    if (a.gewichtSignaal === "BINNEN") return { icoon: <CheckIcon width={16} height={16} />, label, toon: "success" as const };
    return null;
  };

  const metaRowsFor = (a: CardItem): AnimalCardMetaRow[] =>
    META_FIELDS.filter((f) => fields.includes(f) && f !== "gewichtsignaal").map((f) => ({
      key: f,
      icon: FIELD_ICON[f],
      label: t(`dierAanwezig.field.${f}`),
      value: valueOf(a, f),
      pill: f === "status",
    }));

  // Kolommen verschuiven en breder maken: wat je instelt blijft staan, per gebruiker. Welke kolommen
  // er zijn kies je los daarvan met "Velden" -- een kolom die je uitzet verdwijnt, en zijn plek in de
  // indeling blijft gewoon staan voor als je hem weer aanzet.
  const { indeling, setIndeling } = useTabelIndeling("dieren");

  // ── list-mode columns (dynamic on the chosen fields) ──────────────────────────────────────────
  const listColumns: Column<CardItem>[] = useMemo(() => {
    const cols: Column<CardItem>[] = [
      { key: "verblijfNummer", header: t("dierAanwezig.column.verblijfNummer"), cell: (a) => (a.verblijfNummer != null ? `V${a.verblijfNummer}` : "") },
      { key: "dierNaam", header: t("dierAanwezig.column.animalName"), cell: (a) => metNummer(a.registratieNummer, a.naam) },
    ];
    for (const f of META_FIELDS) {
      if (!fields.includes(f)) continue;
      cols.push({ key: f, header: t(`dierAanwezig.field.${f}`), cell: (a) => valueOf(a, f) });
    }
    if (fields.includes("ras")) {
      cols.splice(2, 0, { key: "ras", header: t("dierAanwezig.field.ras"), cell: (a) => subtitleOf(a) });
    }
    if (fields.includes("chipnummer")) {
      cols.push({ key: "chipnummer", header: t("dierAanwezig.field.chipnummer"), cell: (a) => a.chipPasnummer ?? "-" });
    }
    // Foto is a list-only column (Jmix DierAttendanceCardField.FOTO): a leading thumbnail.
    if (fields.includes("foto")) {
      cols.unshift({
        key: "foto",
        header: t("dierAanwezig.field.foto"),
        cell: (a) =>
          photoOf(a) ? (
            <span className="da-overview__thumb">
              <img src={photoOf(a) as string} alt={a.naam ?? ""} />
            </span>
          ) : (
            <span className="da-overview__thumb da-overview__thumb--empty" aria-hidden="true">
              <CameraPawIcon size={18} strokeWidth={1.5} />
            </span>
          ),
      });
    }
    return cols;
  }, [fields, t, locale, photoUrls]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="da-overview">
      {/* Header: title + vestiging subtitle + per-species stat cards (Jmix `.da2-header`) */}
      <div className="da-overview__header">
        <div className="da-overview__intro">
          <span className="da-overview__icon" aria-hidden="true"><PawIcon width={30} height={30} /></span>
          <div>
            <h1 className="da-overview__title">{t(titleKey)}</h1>
            {/* The source's subtitle is a fixed description, not the vestiging name — the shelter is
                already named in the sidebar's vestiging block. */}
            <p className="da-overview__subtitle">{t(subtitleKey)}</p>
          </div>
        </div>
        {showStatCards && (
          <div className="da-overview__stats">
            {(result?.speciesCounts ?? []).map((sc) => (
              <button
                key={sc.id ?? sc.naam}
                type="button"
                className={`da-overview__stat${sc.id != null && speciesIds.includes(sc.id) ? " da-overview__stat--active" : ""}`}
                onClick={() => sc.id != null && toggleSpecies(sc.id)}
              >
                <span className="da-overview__stat-icon">
                  <img src={speciesIconSrc(sc.naam)} alt="" />
                </span>
                <span className="da-overview__stat-body">
                  <span className="da-overview__stat-value">{sc.count}</span>
                  <span className="da-overview__stat-label">{sc.naam}</span>
                </span>
              </button>
            ))}
            <button
              type="button"
              className={`da-overview__stat${speciesIds.length === 0 ? " da-overview__stat--active" : ""}`}
              onClick={() => setSpeciesIds([])}
            >
              <span className="da-overview__stat-icon da-overview__stat-icon--total" aria-hidden="true">
                <PawIcon width={22} height={22} />
              </span>
              <span className="da-overview__stat-body">
                {/* Totaal = alle dieren van het overzicht, los van de filters (wel met de pleeggezinkeuze), zodat hij
                    niet meespringt als je een soort aanklikt (Alma, Roze, 2026-10-09). Wat er na het filteren
                    overblijft staat bij de lijst ("N resultaten"). */}
                <span className="da-overview__stat-value">{result?.totaalAlle ?? total}</span>
                <span className="da-overview__stat-label">{t("dierAanwezig.stat.total")}</span>
              </span>
            </button>
          </div>
        )}
      </div>

      {/* Filters card (Jmix `.da2-toolbar`): full-width search, then a grid of labelled filters. */}
      <div className="da-overview__filters">
        {/* One row: search + the five filters + clear, exactly like the source's `.filterRij`. The
            search sits in the grid's first (wide) column and only wraps full-width at narrow widths. */}
        <div className="da-overview__filter-grid">
          <div className="fawkes-products-search da-overview__search">
            {/* Source `.zoekIcon`: the 🔍 emoji, not a line icon. */}
            <span className="fawkes-products-search__icon" aria-hidden="true">🔍</span>
            <input
              type="search"
              autoComplete="off"
              className="fawkes-products-search__input"
              placeholder={t("dierAanwezig.search.placeholder")}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              aria-label={t("dierAanwezig.search.placeholder")}
            />
          </div>

          {mobiel && (
            <FilterInklapKnop
              open={filtersOpen}
              actief={filterActief}
              label={t("filter.inklap")}
              onClick={wissel}
            />
          )}

          {toon && (
          <>
          <div className="da-overview__field">
            <span className="da-overview__field-label">{t("dierAanwezig.field.diersoort")}</span>
            <SpeciesMultiSelect
              options={speciesOptions}
              selected={speciesIds}
              onToggle={toggleSpecies}
              onClear={() => setSpeciesIds([])}
              allLabel={t("dierAanwezig.filter.all")}
              selectedLabel={(n) => t("dierAanwezig.filter.speciesSelected", { args: [n] })}
            />
          </div>

          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("dierAanwezig.field.ras")}</span>
            <select className="da-overview__filter" value={rasId} onChange={(e) => setRasId(e.target.value)}>
              <option value="">{t("dierAanwezig.filter.all")}</option>
              {breeds.map((b) => <option key={b.id} value={String(b.id)}>{b.label}</option>)}
            </select>
          </label>

          <div className="da-overview__field da-overview__field--age">
            <span className="da-overview__field-label">
              {t("dierAanwezig.filter.age")}
              {ageBand === "RANGE" && !ageOpen && (
                <button type="button" className="da-overview__age-edit" onClick={openAgeRange}>
                  {t("dierAanwezig.filter.age.edit")}
                </button>
              )}
            </span>
            <select
              className="da-overview__filter"
              aria-label={t("dierAanwezig.filter.age")}
              value={ageBand}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "RANGE") {
                  agePrevBand.current = ageBand === "RANGE" ? "ALL" : ageBand;
                  openAgeRange();
                }
                setAgeBand(v);
              }}
            >
              <option value="ALL">{t("dierAanwezig.filter.all")}</option>
              <option value="U3M">{t("dierAanwezig.filter.age.under3m")}</option>
              <option value="U6M">{t("dierAanwezig.filter.age.under6m")}</option>
              <option value="YOUNG">{t("dierAanwezig.filter.age.young")}</option>
              <option value="ADULT">{t("dierAanwezig.filter.age.adult")}</option>
              <option value="SENIOR">{t("dierAanwezig.filter.age.senior")}</option>
              <option value="RANGE">
                {ageBand === "RANGE" && (ageVan || ageTot)
                  ? ageRangeLabel(ageVan, ageVanEenheid, ageTot, ageTotEenheid, t)
                  : t("dierAanwezig.filter.age.range")}
              </option>
            </select>
            {ageOpen && (
              <div
                className="da-overview__age-pop"
                role="dialog"
                aria-label={t("dierAanwezig.filter.age.rangeLabel")}
                ref={agePopRef}
              >
                <div className="da-overview__age-pop-title">{t("dierAanwezig.filter.age.rangeLabel")}</div>
                <div className="da-overview__age-pop-row">
                  <span>{t("dierAanwezig.filter.age.from")}</span>
                <input
                  type="number"
                  min={0}
                  className="da-overview__filter"
                  aria-label={t("dierAanwezig.filter.age.from")}
                  value={ageDraft.van}
                  onChange={(e) => setAgeDraft((d) => ({ ...d, van: e.target.value }))}
                />
                <select
                  className="da-overview__filter"
                  aria-label={t("dierAanwezig.filter.age.unit")}
                  value={ageDraft.vanE}
                  onChange={(e) => setAgeDraft((d) => ({ ...d, vanE: e.target.value as AgeUnit }))}
                >
                  <option value="W">{t("dierAanwezig.filter.age.weeks")}</option>
                  <option value="M">{t("dierAanwezig.filter.age.months")}</option>
                  <option value="J">{t("dierAanwezig.filter.age.years")}</option>
                </select>
                </div>
                <div className="da-overview__age-pop-row">
                  <span>{t("dierAanwezig.filter.age.to")}</span>
                <input
                  type="number"
                  min={0}
                  className="da-overview__filter"
                  aria-label={t("dierAanwezig.filter.age.to")}
                  value={ageDraft.tot}
                  onChange={(e) => setAgeDraft((d) => ({ ...d, tot: e.target.value }))}
                />
                <select
                  className="da-overview__filter"
                  aria-label={t("dierAanwezig.filter.age.unit")}
                  value={ageDraft.totE}
                  onChange={(e) => setAgeDraft((d) => ({ ...d, totE: e.target.value as AgeUnit }))}
                >
                  <option value="W">{t("dierAanwezig.filter.age.weeks")}</option>
                  <option value="M">{t("dierAanwezig.filter.age.months")}</option>
                  <option value="J">{t("dierAanwezig.filter.age.years")}</option>
                </select>
                </div>
                <p className="da-overview__age-hint">{t("dierAanwezig.filter.age.rangeHint")}</p>
                <div className="da-overview__age-pop-actions">
                  <button type="button" className="da-overview__age-cancel" onClick={cancelAgeRange}>
                    {t("dierAanwezig.filter.age.cancel")}
                  </button>
                  <button type="button" className="da-overview__age-apply" onClick={applyAgeRange}>
                    {t("dierAanwezig.filter.age.apply")}
                  </button>
                </div>
              </div>
            )}
          </div>

          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("dierAanwezig.filter.dumped")}</span>
            <select className="da-overview__filter" value={dumped} onChange={(e) => setDumped(e.target.value)}>
              <option value="ALL">{t("dierAanwezig.filter.all")}</option>
              <option value="YES">{t("dierAanwezig.yes")}</option>
              <option value="NO">{t("dierAanwezig.no")}</option>
            </select>
          </label>

          {gewichtFilterAan && (
            <label className="da-overview__field">
              <span className="da-overview__field-label">{t("dierAanwezig.filter.gewicht")}</span>
              <select className="da-overview__filter" value={gewicht} onChange={(e) => { setGewicht(e.target.value); setPage(0); }}>
                <option value="">{t("dierAanwezig.filter.all")}</option>
                <option value="BUITEN">{t("dierAanwezig.gewicht.buiten")}</option>
                <option value="ONDER">{t("dierAanwezig.gewicht.onder")}</option>
                <option value="BOVEN">{t("dierAanwezig.gewicht.boven")}</option>
                <option value="VERLOPEN">{t("dierAanwezig.gewicht.verlopen")}</option>
              </select>
            </label>
          )}

          <label className="da-overview__field">
            <span className="da-overview__field-label">{t("dierAanwezig.filter.minDays")}</span>
            <input
              type="number"
              min={0}
              className="da-overview__filter"
              value={minDays}
              onChange={(e) => setMinDays(e.target.value)}
            />
          </label>

          <div className="da-overview__field da-overview__field--action">
            <span className="da-overview__field-label" aria-hidden="true">&nbsp;</span>
            {/* Source `.wisBtn`: white pill + green-dark bold text + ↻ icon (not the grey secondary Button). */}
            <button type="button" className="da-overview__wis" onClick={resetFilters}>
              <span className="da-overview__wis-icon" aria-hidden="true">↻</span>
              {t("dierAanwezig.filter.clear")}
            </button>
          </div>
          </>
          )}
        </div>


        {/* De tweede rij: periodes, hoe het dier vertrok en waar het is aangetroffen. Ingeklapt tot je
            hem openzet — zelden gebruikte filters horen de balk niet permanent twee rijen hoog te
            maken. Het aantal actieve filters staat op de knop, zodat een gezet filter nooit onzichtbaar
            achter een dichtgeklapte rij blijft staan. */}
        {toon && (
        <button
          type="button"
          className="da-overview__meer"
          aria-expanded={meerOpen}
          onClick={() => setMeerOpen((o) => !o)}
        >
          <span aria-hidden="true">{meerOpen ? "▴" : "▾"}</span>
          {t("dierAanwezig.filter.more")}
          {meerActief > 0 && <span className="da-overview__meer-badge">{meerActief}</span>}
        </button>
        )}

        {toon && meerOpen && (
          <div className="da-overview__filter-grid da-overview__filter-grid--meer">
            <label className="da-overview__field">
              <span className="da-overview__field-label">{t("dierAanwezig.filter.opnameVan")}</span>
              <DateField className="da-overview__filter" value={opnameVan}
                onChange={(e) => setOpnameVan(e.target.value)} />
            </label>
            <label className="da-overview__field">
              <span className="da-overview__field-label">{t("dierAanwezig.filter.opnameTot")}</span>
              <DateField className="da-overview__filter" value={opnameTot}
                onChange={(e) => setOpnameTot(e.target.value)} />
            </label>
            {showDepartureFilters && (
              <>
                <label className="da-overview__field">
                  <span className="da-overview__field-label">{t("dierAanwezig.filter.vertrekVan")}</span>
                  <DateField className="da-overview__filter" value={vertrekVan}
                    onChange={(e) => setVertrekVan(e.target.value)} />
                </label>
                <label className="da-overview__field">
                  <span className="da-overview__field-label">{t("dierAanwezig.filter.vertrekTot")}</span>
                  <DateField className="da-overview__filter" value={vertrekTot}
                    onChange={(e) => setVertrekTot(e.target.value)} />
                </label>
                <label className="da-overview__field">
                  <span className="da-overview__field-label">{t("dierAanwezig.filter.vertrekSoort")}</span>
                  <select className="da-overview__filter" value={vertrekSoortId}
                    onChange={(e) => setVertrekSoortId(e.target.value)}>
                    <option value="">{t("dierAanwezig.filter.all")}</option>
                    {vertrekSoorten.map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
                  </select>
                </label>
              </>
            )}
            <label className="da-overview__field">
              <span className="da-overview__field-label">{t("dierAanwezig.filter.gemeente")}</span>
              <select className="da-overview__filter" value={gemeenteId}
                onChange={(e) => setGemeenteId(e.target.value)}>
                <option value="">{t("dierAanwezig.filter.all")}</option>
                {gemeenten.map((o) => <option key={o.id} value={String(o.id)}>{o.label}</option>)}
              </select>
            </label>
          </div>
        )}

        {toon && showFosterToggle && fosterModuleEnabled && (
          <div className="da-overview__foster">
            <PleegKeuze waarde={pleeg} aantal={result?.fosterCount ?? 0} onKies={setPleeg} />
          </div>
        )}
      </div>

      {/* Results sub-bar (Jmix bottom bar): count · Velden · view toggle · sort. */}
      <div className="da-overview__resultsbar">
        <span className="da-overview__results-count">
          {/* The source singularises: "1 resultaat gevonden", not "1 resultaten gevonden". */}
          <PawIcon width={16} height={16} /> {total}{" "}
          {t(total === 1 ? "dierAanwezig.results.foundOne" : "dierAanwezig.results.found")}
        </span>
        <div className="da-overview__resultsbar-right">
          {/* Source `.veldenBtn`: bordered white button with a green gear icon (not a bare text link). */}
          <button type="button" className="da-overview__velden" onClick={() => setChooserOpen(true)}>
            <GearIcon className="da-overview__velden-cog" width={16} height={16} />
            {t("dierAanwezig.fieldChooser.open")}
          </button>
          <div className="da-overview__viewtoggle" role="group" aria-label={t("dierAanwezig.viewMode")}>
            <button type="button" className={btnClass(viewMode === "card")} onClick={() => persistMode("card")} title={t("dierAanwezig.cardMode")} aria-label={t("dierAanwezig.cardMode")} aria-pressed={viewMode === "card"}><span aria-hidden="true">▦</span></button>
            <button type="button" className={btnClass(viewMode === "compact")} onClick={() => persistMode("compact")} title={t("dierAanwezig.compactMode")} aria-label={t("dierAanwezig.compactMode")} aria-pressed={viewMode === "compact"}><span aria-hidden="true">▥</span></button>
            <button type="button" className={btnClass(viewMode === "list")} onClick={() => persistMode("list")} title={t("dierAanwezig.listMode")} aria-label={t("dierAanwezig.listMode")} aria-pressed={viewMode === "list"}><span aria-hidden="true">☰</span></button>
          </div>
          <label className="da-overview__sort">
            <span className="da-overview__sort-label">{t("dierAanwezig.sortLabel")}</span>
            <select className="da-overview__filter" value={sort} onChange={(e) => setSort(e.target.value)}>
              {/* "Nieuwste / oudste eerst" loopt op de opnamedatum, op Vertrokken op de vertrekdatum. */}
              <option value="NEWEST">
                {t(showDepartureFilters ? "dierAanwezig.sort.departedNewest" : "dierAanwezig.sort.newest")}
              </option>
              <option value="OLDEST">
                {t(showDepartureFilters ? "dierAanwezig.sort.departedOldest" : "dierAanwezig.sort.oldest")}
              </option>
              <option value="NAME">{t("dierAanwezig.sort.name")}</option>
              <option value="NAME_DESC">{t("dierAanwezig.sort.nameDesc")}</option>
              <option value="YOUNGEST">{t("dierAanwezig.sort.youngest")}</option>
              <option value="OLDEST_AGE">{t("dierAanwezig.sort.oldestAge")}</option>
              <option value="SPECIES">{t("dierAanwezig.sort.species")}</option>
              <option value="VERBLIJFNR">{t("dierAanwezig.sort.verblijfnr")}</option>
            </select>
          </label>
        </div>
      </div>

      {/* Body. Tijdens een verversing blijft de vorige lijst staan, iets gedimd — zo zie je dat er
          iets gebeurt zonder dat het scherm leegslaat. */}
      <div className={isFetching && !isLoading ? "da-overview__body da-overview__body--verversen" : "da-overview__body"}>
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("dierAanwezig.loadFailed")}</p>
      ) : items.length === 0 ? (
        <p className="fawkes-muted">{t("dierAanwezig.empty")}</p>
      ) : viewMode === "list" ? (
        <DataTable columns={listColumns} rows={items} getRowKey={(a) => String(a.verblijfId)}
          onRowClick={(a) => onOpenAnimal(String(a.id))} empty={t("dierAanwezig.empty")}
          indeling={indeling} onIndelingChange={setIndeling} />
      ) : (
        <div className={`da-overview__grid${viewMode === "compact" ? " da-overview__grid--compact" : ""}`}>
          {items.map((a) => {
            const chip = a.chipPasnummer ?? "";
            return (
              <AnimalCard
                key={a.verblijfId}
                compact={viewMode === "compact"}
                name={metNummer(a.registratieNummer, a.naam)}
                subtitle={fields.includes("ras") ? subtitleOf(a) : undefined}
                photoUrl={photoOf(a)}
                noPhotoLabel={t("dierAanwezig.card.noPhoto")}
                badgeNumber={a.verblijfNummer != null ? `V${a.verblijfNummer}` : null}
                statusBadge={statusText(a)}
                foster={a.foster}
                fosterLabel={t("dierAanwezig.card.foster")}
                meta={metaRowsFor(a)}
                signaal={gewichtSignaalVan(a)}
                chipLabel={fields.includes("chipnummer") ? t("dierAanwezig.card.chipLabel") : undefined}
                chipValue={chip || null}
                chipHref={chip ? `${CHIP_LOOKUP}${encodeURIComponent(chip)}` : null}
                detailsLabel={t("dierAanwezig.card.details")}
                onOpen={() => onOpenAnimal(String(a.id))}
              />
            );
          })}
        </div>
      )}
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="da-overview__pager">
          <Button variant="secondary" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹</Button>
          <span className="da-overview__pager-label">{page + 1} / {pageCount}</span>
          <Button variant="secondary" disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>›</Button>
        </div>
      )}

      {/* Field chooser */}
      {chooserOpen && (
        <Modal open title={t("dierAanwezig.fieldChooser.title")} onClose={() => setChooserOpen(false)}>
          <div className="da-overview__chooser">
            {/* `foto` is only meaningful as a list column (Jmix: hidden in card/compact mode). */}
            {ALL_FIELDS.filter((f) => f !== "foto" || viewMode === "list").map((f) => (
              <CheckboxField
                key={f}
                label={t(`dierAanwezig.field.${f}`)}
                checked={fields.includes(f)}
                onChange={(e) =>
                  setFields((cur) => {
                    const checked = e.target.checked;
                    const next = checked ? [...cur, f] : cur.filter((x) => x !== f);
                    const ordered = ALL_FIELDS.filter((x) => next.includes(x));
                    try {
                      localStorage.setItem(`${prefsKey}.fields`, ordered.join(","));
                    } catch {
                      /* ignore */
                    }
                    return ordered;
                  })
                }
              />
            ))}
          </div>
          <div className="da-overview__chooser-actions">
            <Button onClick={() => setChooserOpen(false)}>{t("common.close")}</Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function btnClass(active: boolean) {
  return active ? "da-overview__viewbtn da-overview__viewbtn--active" : "da-overview__viewbtn";
}

function readMode(prefsKey: string): ViewMode {
  try {
    const v = localStorage.getItem(`${prefsKey}.viewMode`);
    if (v === "card" || v === "compact" || v === "list") return v;
  } catch {
    /* ignore */
  }
  return "card";
}

function readFields(prefsKey: string): FieldKey[] {
  try {
    const v = localStorage.getItem(`${prefsKey}.fields`);
    if (v) {
      const parsed = v.split(",").filter((x) => (ALL_FIELDS as readonly string[]).includes(x)) as FieldKey[];
      if (parsed.length) return ALL_FIELDS.filter((f) => parsed.includes(f));
    }
  } catch {
    /* ignore */
  }
  return DEFAULT_FIELDS;
}

/** Map a species name to its stat-card illustration (paw fallback), mirroring Jmix `speciesIconSrc`. */
/**
 * "1187393 Bo" — het dierregistratienummer voor de naam. Elk dier in de opvang hoort er een te hebben
 * (de intake deelt hem uit); een dier van vóór die regel toont gewoon zijn naam.
 */
export function metNummer(registratieNummer: number | null, naam: string | null): string {
  const nr = registratieNummer == null ? "" : String(registratieNummer);
  const n = naam ?? "";
  return [nr, n].filter(Boolean).join(" ") || "-";
}

/**
 * De afbeelding bij een diersoort. Staat hier omdat het dierenoverzicht hem het eerst had, en
 * wordt geexporteerd omdat de tariefschermen dezelfde rij tegels tonen — twee mappings die uit
 * elkaar lopen is precies hoe schermen ongelijk worden.
 */
export function speciesIconSrc(naam: string | null): string {
  const n = (naam ?? "").toLowerCase();
  let file = "overig";
  if (n.includes("hond")) file = "hond";
  else if (n.includes("kat")) file = "kat";
  else if (n.includes("konijn")) file = "konijn";
  else if (n.includes("vogel")) file = "vogel";
  else if (n.includes("knaag")) file = "knaagdier";
  return `/species/${file}.png`;
}

/**
 * Jmix formatRaceValue: kruising → "name (kruising)" / "kruising"; else the breed name. When there is no
 * breed the source card falls back to the species and then "Onbekend ras" (never a bare "-").
 */
function formatRace(
  ras: string | null,
  kruising: boolean,
  diersoort: string | null,
  t: (k: string) => string,
): string {
  const name = ras && ras.trim() ? ras : "";
  if (kruising) return name ? `${name} (${t("dierAanwezig.kruising")})` : t("dierAanwezig.kruising");
  return name || diersoort || t("dierAanwezig.onbekendRas");
}

type AgeUnit = "W" | "M" | "J";

/** Wat de keuzelijst toont na "Toepassen": "3 – 6 mnd", "vanaf 1 jr", "tot 6 mnd". */
export function ageRangeLabel(
  van: string,
  vanE: AgeUnit,
  tot: string,
  totE: AgeUnit,
  t: (key: string) => string,
): string {
  const unit = (u: AgeUnit) =>
    t(
      u === "J"
        ? "dierAanwezig.filter.age.yearsShort"
        : u === "W"
          ? "dierAanwezig.filter.age.weeksShort"
          : "dierAanwezig.filter.age.monthsShort",
    );
  if (van && tot) {
    return vanE === totE ? `${van} – ${tot} ${unit(totE)}` : `${van} ${unit(vanE)} – ${tot} ${unit(totE)}`;
  }
  if (van) return `${t("dierAanwezig.filter.age.fromShort")} ${van} ${unit(vanE)}`;
  return `${t("dierAanwezig.filter.age.toShort")} ${tot} ${unit(totE)}`;
}

/** Eén grens van het leeftijdsbereik, in weken of maanden (jaren gaan als maanden naar de api). */
export type AgeBound = { value: number; unit: "W" | "M" } | null;

/**
 * Leeftijdsfilter naar grenzen voor de api (van inclusief, tot exclusief), of null als de vaste band
 * (ageBand) volstaat. "Tussen…" met twee lege velden filtert niet; elke grens houdt zijn eigen eenheid.
 */
export function ageBandBounds(
  band: string,
  van: string,
  vanEenheid: AgeUnit,
  tot: string,
  totEenheid: AgeUnit,
): { from: AgeBound; to: AgeBound } | null {
  if (band === "U3M") return { from: null, to: { value: 3, unit: "M" } };
  if (band === "U6M") return { from: null, to: { value: 6, unit: "M" } };
  if (band !== "RANGE") return null;
  const grens = (v: string, unit: AgeUnit): AgeBound => {
    const n = Number(v.replace(",", "."));
    if (v.trim() === "" || !Number.isFinite(n) || n < 0) return null;
    if (unit === "W") return { value: Math.round(n), unit: "W" };
    return { value: Math.round(unit === "J" ? n * 12 : n), unit: "M" };
  };
  return { from: grens(van, vanEenheid), to: grens(tot, totEenheid) };
}

/** Jmix formatAgeValue: <14d "Nd"; <90d "Nw Md"; <365d "Nmnd Md"; else "Nj Mmnd". */
function formatAge(birth: string | null): string | null {
  if (!birth) return null;
  const b = new Date(birth);
  const today = new Date();
  const totalDays = Math.floor((today.getTime() - b.getTime()) / 86_400_000);
  if (totalDays < 0) return null;
  if (totalDays < 14) return `${totalDays}d`;
  if (totalDays < 90) return `${Math.floor(totalDays / 7)}w ${totalDays % 7}d`;
  let years = today.getFullYear() - b.getFullYear();
  let months = today.getMonth() - b.getMonth();
  let days = today.getDate() - b.getDate();
  if (days < 0) months -= 1;
  if (months < 0) {
    months += 12;
    years -= 1;
  }
  if (totalDays < 365) return `${years * 12 + months}mnd ${Math.max(0, days)}d`;
  return `${years}j ${months}mnd`;
}
